// The show view's words, written here and never by the API, which returns the things themselves.
// Pure, tested (words.test.ts).
//
// A show's times are the SHOW'S clock, not the reader's: a meeting at the stand at 11:00 is 11:00 in
// Lisbon whether the reader opens the page in Lisbon, Malta or London, and the tracker writes it that
// way. So every clock below is read in the show's own zone, from the instant the store holds, and the
// page says once which city's time it is. The month is spelled from apps/portal/src/day.ts's table
// ("one day, one spelling"), never asked of Intl.
import { MONTHS_SHORT } from '../day.js';
import type { Debrief, FollowUp, ShowHead, ShowMeeting } from './api.js';

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

interface Parts { y: number; m: number; d: number; hh: string; mm: string }

/** An instant's calendar day and clock in a zone, numbers only (Intl for the offset, never for a name). */
export function partsIn(iso: string, timeZone: string): Parts {
  const f = new Intl.DateTimeFormat('en-GB', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const p = Object.fromEntries(f.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { y: Number(p.year), m: Number(p.month), d: Number(p.day), hh: p.hour ?? '00', mm: p.minute ?? '00' };
}

const weekdayOf = (y: number, m: number, d: number): number => new Date(Date.UTC(y, m - 1, d)).getUTCDay();

/** "11:00", on the show's clock. */
export function clockOf(iso: string, timeZone: string): string {
  const p = partsIn(iso, timeZone);
  return `${p.hh}:${p.mm}`;
}

/** "Tue 29 Sep, 11:00", on the show's clock: the clock plate. */
export function plateOf(iso: string, timeZone: string): string {
  const p = partsIn(iso, timeZone);
  return `${WEEKDAYS[weekdayOf(p.y, p.m, p.d)]!.slice(0, 3)} ${p.d} ${MONTHS_SHORT[p.m - 1]}, ${p.hh}:${p.mm}`;
}

/** "Tuesday 29 September", from a calendar date the store holds as YYYY-MM-DD. */
export function dayHeading(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return `${WEEKDAYS[weekdayOf(y, m, d)]} ${d} ${MONTHS_LONG[m - 1]}`;
}

/** "29 Sep to 1 Oct"; one day is one date. */
export function showDates(startsOn: string, endsOn: string): string {
  const one = (s: string) => { const [, m, d] = s.split('-').map(Number) as [number, number, number]; return `${d} ${MONTHS_SHORT[m - 1]}`; };
  return startsOn === endsOn ? one(startsOn) : `${one(startsOn)} to ${one(endsOn)}`;
}

/** "Lisbon", from "Europe/Lisbon": the city whose clock the page reads. */
export function cityOfZone(timeZone: string): string {
  return (timeZone.split('/').pop() ?? timeZone).replace(/_/g, ' ');
}

/**
 * Ours, as the store names them: a colleague the tracker's words resolved to by the name Cobrain holds
 * for them, and a name that resolved to nobody exactly as the tracker writes it (the brief: an
 * unresolved rep is kept as written, never guessed at). Nobody at all is "Not assigned".
 */
export function oursOf(m: Pick<ShowMeeting, 'primaryName' | 'primaryRepText' | 'secondName' | 'secondRepText'>): string[] {
  const out: string[] = [];
  const a = m.primaryName ?? m.primaryRepText;
  const b = m.secondName ?? m.secondRepText;
  if (a && a.trim()) out.push(a.trim());
  if (b && b.trim() && b.trim() !== a?.trim()) out.push(b.trim());
  return out;
}

/** What they are interested in, as the tracker writes it. */
export function interestedOf(products: string[]): string {
  const clean = products.map((p) => p.trim()).filter(Boolean);
  return clean.length ? clean.join(', ') : 'Not stated';
}

/** The first word of a name: the clash line names a colleague the way the team says it. */
const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name;

/** The company as the page shows it: the organisation Cobrain holds where it matched, else the tracker's words. */
export function companyOf(m: Pick<ShowMeeting, 'organisationName' | 'companyLabel'>): string {
  return m.organisationName ?? m.companyLabel ?? 'Company not stated';
}

/**
 * The words on a clash (U161), said on both rows: "Vincenzo also at Retabet, 11:30". Each clash names
 * the colleague and the other meeting, so two rows that clash read as each other's mirror.
 */
export function clashWords(clash: { who: string; otherMeetingId: string }, byId: Map<string, ShowMeeting>, timeZone: string): string {
  const other = byId.get(clash.otherMeetingId);
  const where = other ? companyOf(other) : 'another meeting';
  const when = other?.startsAt ? `, ${clockOf(other.startsAt, timeZone)}` : '';
  return `${firstName(clash.who)} also at ${where}${when}`;
}

/**
 * When the tracker's last read failed after a good one, the page still shows the good one and says
 * so in words (the brief, section 8). A failure with no good read ever is the same sentence without
 * the time. No failure newer than the last good read says nothing.
 */
export function readFailedWords(show: Pick<ShowHead, 'lastReadOkAt' | 'lastReadError' | 'lastReadAt'>, format: (iso: string) => string): string | null {
  if (!show.lastReadError || !show.lastReadAt) return null;
  if (show.lastReadOkAt && Date.parse(show.lastReadOkAt) >= Date.parse(show.lastReadAt)) return null;
  return show.lastReadOkAt
    ? `The tracker could not be read. What was read at ${format(show.lastReadOkAt)} is shown.`
    : 'The tracker could not be read.';
}

/** Group meetings by the show day they fall on, in order; a meeting with no day goes last. */
export function byDay(meetings: ShowMeeting[]): Array<{ day: string | null; meetings: ShowMeeting[] }> {
  const groups = new Map<string | null, ShowMeeting[]>();
  for (const m of meetings) {
    const k = m.meetingOn ?? null;
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(m);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : a.localeCompare(b)))
    .map(([day, ms]) => ({ day, meetings: ms }));
}

/**
 * The follow-up in words: "Sent Tue 29 Sep, 16:10" on the show's clock, or "Not sent, 5 hours" (days past two days).
 * Null draws nothing, which is what the API says whenever the absence of a mail could not be known.
 */
export function followUpWords(f: FollowUp | null, timeZone: string): { text: string; tone: 'ok' | 'warn' } | null {
  if (!f) return null;
  if (f.kind === 'sent') return { text: `Sent ${plateOf(f.at, timeZone)}`, tone: 'ok' };
  const h = f.hours;
  const span = h < 1 ? 'under an hour' : h < 48 ? `${h} hour${h === 1 ? '' : 's'}` : `${Math.floor(h / 24)} days`;
  return { text: `Not sent, ${span}`, tone: 'warn' };
}

/** A recording's running clock: "0:07", "1:42", "10:00". */
export function clockOfSeconds(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * What a debrief row says above its words. The reader's own debrief says where it is; a colleague's is only
 * ever drawn once its words can be read, so it says who spoke it and when.
 */
export function debriefHead(d: Pick<Debrief, 'isReaders' | 'authorName' | 'state' | 'recordedAt'>, timeZone: string): string {
  const who = d.isReaders ? 'Your debrief' : `Debrief by ${d.authorName ?? 'a colleague'}`;
  return `${who}, ${plateOf(d.recordedAt, timeZone)}`;
}

/** The reader's own debrief while it is not yet words, or when it could not be made into words. */
export function debriefStateWords(d: Pick<Debrief, 'state' | 'error' | 'words'>): { text: string; tone: 'quiet' | 'warn' } | null {
  if (d.state === 'failed') return { text: d.error ? `Not turned into text: ${d.error}` : 'Not turned into text.', tone: 'warn' };
  if (d.state === 'waiting' || d.state === 'transcribing') return { text: 'Received. Turning it into text.', tone: 'quiet' };
  if (d.state === 'done' && d.words === null) return { text: 'Turned into text.', tone: 'quiet' };
  return null;
}
